/* RESPONSIBLE TEAM: team-ai-insights */
/* ToDo: Localize these strings when the strings are finalized */
/* eslint-disable @intercom/intercom/no-bare-strings */
import { type LogicalFilter, type Filter } from 'embercom/components/reporting/custom/filters';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type Aggregation } from 'embercom/objects/reporting/unified/metrics/types';
import type IntlService from 'ember-intl/services/intl';
import type { SummaryTab } from './conversation-explore-panel';

export interface ChartSeriesDefinitionWithoutLabel {
  metric_id: string;
  aggregation?: Aggregation;
  tooltip?: string;
  filters?: LogicalFilter | Filter;
}

export interface ChartSeriesDefinition extends ChartSeriesDefinitionWithoutLabel {
  label: string;
}

export function insightsMetrics(intl: IntlService): Record<string, ChartSeriesDefinition> {
  return {
    volume: {
      metric_id: 'v1.new_conversations',
      aggregation: 'count',
      label: intl.t('components.reporting.automation.fin-insights.new-conversations'),
    },
    resolutionRate: {
      metric_id: 'fin.resolution_rate',
      label: intl.t('components.reporting.automation.fin-insights.resolution-rate'),
    },
    involvementRate: {
      metric_id: 'fin.participation_rate',
      label: intl.t('components.reporting.automation.fin-insights.involvement-rate'),
    },
    involvedConversations: {
      metric_id: 'fin.conversations_participated.count',
      label: intl.t('components.reporting.automation.fin-insights.involved-conversations'),
    },
    finOnlyRatedConversations: {
      metric_id:
        'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
      label: intl.t('components.reporting.automation.fin-insights.fin-only-conversations'),
      filters: {
        type: 'and',
        filters: [
          {
            type: 'category',
            data: {
              property: 'agent_types_involved_grouping',
              attribute: 'conversation.agent_types_involved_grouping',
              values: ['ai-agent'],
            },
          },
        ],
      },
    },
  };
}

function cxFilter(values: string[]) {
  return {
    type: 'category',
    data: {
      property: 'conversation_custom_fields#customer_experience_attributes',
      attribute: 'conversation_custom_fields#customer_experience_attributes',
      values,
    },
  };
}

function escalationFilter(values: string[]) {
  return {
    type: 'category',
    data: {
      property: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      attribute: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      values,
    },
  };
}

function involvementFilter(values: string[]) {
  return {
    type: 'category',
    data: {
      property: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      attribute: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      values,
    },
  };
}

const filters = {
  get contentGaps() {
    return escalationFilter(['content_gap']);
  },
  get dataGaps() {
    return escalationFilter(['data_gap']);
  },
  get actionGaps() {
    return escalationFilter(['action_gap']);
  },
  get investigationRequired() {
    return escalationFilter(['investigation_needed']);
  },
  get businessLogic() {
    return escalationFilter(['business_logic']);
  },
  get customerDistrust() {
    return escalationFilter(['customer_distrust']);
  },
  get negativeAiAgentAnswerQuality() {
    return cxFilter(['answer_quality_bot:low']);
  },
  get highCustomerEffort() {
    return cxFilter(['user_effort:high']);
  },
  get negativePolicyFeedback() {
    return cxFilter(['policy_feedback:negative']);
  },
  get negativeProductFeedback() {
    return cxFilter(['product_feedback:negative']);
  },
  get negativeCustomerImpact() {
    return cxFilter(['customer_impact:negative']);
  },
  get negativeStrongEmotions() {
    return cxFilter(['user_strong_emotion:negative']);
  },
  get channelGaps() {
    return involvementFilter(['channel']);
  },
  get audienceTargeting() {
    return involvementFilter(['audience_targeting']);
  },
  get scheduling() {
    return involvementFilter(['scheduling']);
  },
  get branchingLogic() {
    return involvementFilter(['branching_logic']);
  },
};

function jobDefinitions(intl: IntlService) {
  let metrics = insightsMetrics(intl);

  return [
    {
      id: 'contentGaps',
      label: 'Content gaps',
      description: 'Missing or incomplete content causing escalation to human agents',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.contentGaps),
      attributesValues: ['content_gap'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'dataGaps',
      label: 'Data gaps',
      description: 'Lack of access to data causes escalations to human agents',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.dataGaps),
      attributesValues: ['data_gap'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'actionGaps',
      label: 'Action gaps',
      description: 'Fin is unable to complete intended actions through self-service',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.actionGaps),
      attributesValues: ['action_gap'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'investigationRequired',
      label: 'Investigation required',
      description: 'Complex queries requiring human research and follow-up',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.investigationRequired),
      attributesValues: ['investigation_needed'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'businessLogic',
      label: 'Business logic',
      description: 'System constraints preventing optimal resolution paths',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.businessLogic),
      attributesValues: ['business_logic'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'customerDistrust',
      label: 'Customer distrust',
      description: 'Users questioning accuracy or reliability of Fin responses',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.customerDistrust),
      attributesValues: ['customer_distrust'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'negativeAiAgentAnswerQuality',
      label: 'Low Fin answer quality',
      description: 'Missing or incomplete content causing escalation to human agents',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeAiAgentAnswerQuality),
      attributesValues: ['answer_quality_bot:low'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'highCustomerEffort',
      label: 'High customer effort',
      description: 'Customers must repeat themselves, clarify questions, or take extra steps',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.highCustomerEffort),
      attributesValues: ['user_effort:high'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativeProductFeedback',
      label: 'Negative product feedback',
      description: 'Missing features or usability issues make the product hard to use',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeProductFeedback),
      attributesValues: ['product_feedback:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativePolicyFeedback',
      label: 'Negative policy feedback',
      description: 'Customers feel policies are unclear, unfair, or overly restrictive',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativePolicyFeedback),
      attributesValues: ['policy_feedback:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativeCustomerImpact',
      label: 'Negative customer impact',
      description: 'Product issues or outages stop users from getting work done',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeCustomerImpact),
      attributesValues: ['customer_impact:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativeStrongEmotions',
      label: 'Negative strong emotions',
      description: 'Frustration caused by poor support, unmet needs, or urgent problems',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeStrongEmotions),
      attributesValues: ['user_strong_emotion:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_subtopic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'channelGaps',
      label: 'Channel gaps',
      description:
        "Fin wasn't live on the conversation's channel at the time the conversation was started",
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.channelGaps),
      attributesValues: ['channel'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
    {
      id: 'audienceTargeting',
      label: 'Audience targeting',
      description:
        "Fin was live on the conversation's channel at the time the conversation was started, but it didn't match the current conversation",
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.audienceTargeting),
      attributesValues: ['audience_targeting'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
    {
      id: 'scheduling',
      label: 'Scheduling',
      description:
        "Fin was live on the conversation's channel but via a workflow that wasn't scheduled at the time the conversation was started",
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.scheduling),
      attributesValues: ['scheduling'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
    {
      id: 'branchingLogic',
      label: 'Branching logic',
      description: "A Fin workflow did get triggered, but Fin didn't get involved",
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.branchingLogic),
      attributesValues: ['branching_logic'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
  ];
}

type JobId = ReturnType<typeof jobDefinitions>[number]['id'];

interface Job {
  id: JobId;
  label: string;
  description: string;
  showSuggestions: boolean;
  backgroundClass: string;
  chartSeries: readonly ChartSeriesDefinition[];
  filters: LogicalFilter;
  attributesValues: readonly string[];
  attributeId: string;
  breakdownByAttributeId?: string;
  summaryType: SummaryTab;
}

interface JobGroupObject {
  id: string;
  label: string;
  header?: string;
  chartSeries: ChartSeriesDefinitionWithoutLabel[];
  backgroundClass: string;
  borderClass: string;
  fontClass: string;
  positiveLabel: string;
  negativeLabel: string;
  positiveColor: string;
  negativeColor: string;
}

const jobGroups: Record<string, readonly JobId[]> = {
  improveAnswers: [
    'contentGaps',
    'dataGaps',
    'customerDistrust',
    'actionGaps',
    'investigationRequired',
    'businessLogic',
  ],
  checkQuality: [
    'negativeAiAgentAnswerQuality',
    'highCustomerEffort',
    'negativeProductFeedback',
    'negativePolicyFeedback',
    'negativeCustomerImpact',
    'negativeStrongEmotions',
  ],
  increaseCoverage: ['channelGaps', 'audienceTargeting', 'scheduling', 'branchingLogic'],
};

const jobGroupObjects: JobGroupObject[] = [
  {
    label: 'components.reporting.automation.fin-insights.job-groups.improve-answers',
    header: 'components.reporting.automation.fin-insights.job-groups.headers.improve-answers',
    chartSeries: [{ metric_id: 'fin.resolution_rate' }],
    backgroundClass: 'bg-vis-green-50',
    borderClass: 'border-vis-green-50',
    fontClass: 'text-default',
    positiveLabel: 'Resolved',
    negativeLabel: 'Escalated',
    positiveColor: 'text-success',
    negativeColor: 'text-accent',
    id: 'improveAnswers',
  },
  {
    label: 'components.reporting.automation.fin-insights.job-groups.check-quality',
    header: 'components.reporting.automation.fin-insights.job-groups.headers.check-quality',
    chartSeries: [
      {
        metric_id:
          'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
        filters: {
          type: 'and',
          filters: [
            {
              type: 'category',
              data: {
                property: 'agent_types_involved_grouping',
                attribute: 'conversation.agent_types_involved_grouping',
                values: ['ai-agent'],
              },
            },
          ],
        },
      },
    ],
    backgroundClass: 'bg-vis-violet-50',
    borderClass: 'border-vis-violet-50',
    fontClass: 'text-on-neutral',
    positiveLabel: 'Positive CX Score',
    negativeLabel: 'Negative',
    positiveColor: 'reporting__text-vis-violet-50',
    negativeColor: 'text-accent',
    id: 'checkQuality',
  },
  {
    label: 'components.reporting.automation.fin-insights.job-groups.increase-coverage',
    header: 'components.reporting.automation.fin-insights.job-groups.headers.increase-coverage',
    chartSeries: [{ metric_id: 'fin.participation_rate' }],
    backgroundClass: 'bg-vis-blue-50',
    borderClass: 'border-vis-blue-50',
    fontClass: 'text-on-neutral',
    positiveLabel: 'Involved',
    negativeLabel: 'Non-involved',
    positiveColor: 'reporting__text-vis-blue-50',
    negativeColor: 'text-accent',
    id: 'increaseCoverage',
  },
];
function jobs(intl: IntlService) {
  return jobDefinitions(intl) as Job[];
}

export { jobGroups, jobGroupObjects, jobs, type JobId, type Job, type JobGroupObject };
